import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Card from '../../../components/base/Card';
import Button from '../../../components/base/Button';

export default function TradingWidget() {
  const { t } = useTranslation();
  const [fromAmount, setFromAmount] = useState('300');
  const [toAmount, setToAmount] = useState('300.00');
  const [fromCurrency, setFromCurrency] = useState('USD');
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const [exchangeRates, setExchangeRates] = useState<Record<string, number>>({});
  const [isLoadingRates, setIsLoadingRates] = useState(false);

  const currencies = [
    { symbol: 'USD', name: 'US Dollar', icon: 'https://asset.xcoins.com/5ea769c4-4771-4ea6-bd65-16f97a212e7b.svg' },
    { symbol: 'EUR', name: 'Euro', icon: 'https://asset.xcoins.com/f039066a-c440-425d-a35c-c49cdd3b4f7f.svg' },
    { symbol: 'JPY', name: 'Japanese Yen', icon: 'https://hatscripts.github.io/circle-flags/flags/jp.svg' },
    { symbol: 'GBP', name: 'British Pound', icon: 'https://asset.xcoins.com/ecc40404-ad7c-44f7-b98a-c3b131f83b01.svg' },
    { symbol: 'CAD', name: 'Canadian Dollar', icon: 'https://hatscripts.github.io/circle-flags/flags/ca.svg' }
  ];

  // Fetch exchange rates
  const fetchExchangeRates = async () => {
    setIsLoadingRates(true);
    try {
      const response = await fetch('https://api.exchangerate-api.com/v4/latest/USD');
      if (!response.ok) {
        throw new Error('API response not ok');
      }
      const data = await response.json();
      // Add 10.1% markup to all exchange rates
      const markedUpRates: Record<string, number> = {};
      Object.keys(data.rates).forEach(currency => {
        markedUpRates[currency] = data.rates[currency] * 1.101; // 10.1% markup
      });
      setExchangeRates(markedUpRates);
    } catch (error) {
      // Silently use fallback rates with 10.1% markup if API fails
      setExchangeRates({
        USD: 1.101,
        EUR: 0.9224 * 1.101 / 1.0525,
        JPY: 119.52 * 1.101 / 1.0525,
        GBP: 0.7945 * 1.101 / 1.0525,
        CAD: 1.3599 * 1.101 / 1.0525
      });
    } finally {
      setIsLoadingRates(false);
    }
  };

  // Calculate USDT amount based on currency and exchange rate (0% fee)
  const calculateUSDTAmount = (amount: string, currency: string) => {
    const numAmount = parseFloat(amount) || 0;
    
    const rate = exchangeRates[currency];
    if (rate) {
      const usdAmount = numAmount / rate;
      return usdAmount.toFixed(2);
    }
    
    return '0.00';
  };

  // Update USDT amount when currency or amount changes
  useEffect(() => {
    if (Object.keys(exchangeRates).length > 0) {
      setToAmount(calculateUSDTAmount(fromAmount, fromCurrency));
    }
  }, [fromAmount, fromCurrency, exchangeRates]);

  // Fetch rates on component mount
  useEffect(() => {
    fetchExchangeRates();
    // Refresh rates every 30 seconds
    const interval = setInterval(fetchExchangeRates, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleCurrencySelect = (currency: string) => {
    setFromCurrency(currency);
    setShowCurrencyDropdown(false);
  };

  const handleAmountChange = (value: string) => {
    setFromAmount(value);
  };

  const getCurrentCurrencyIcon = () => {
    return currencies.find(c => c.symbol === fromCurrency)?.icon || currencies[0].icon;
  };

  const getCurrentExchangeRate = () => {
    if (fromCurrency === 'USD') return 1.101; // Show 10.1% markup for USD too
    return exchangeRates[fromCurrency] || 1;
  };

  const scrollToTradingWidget = () => {
    const tradingSection = document.querySelector('#trading-widget');
    if (tradingSection) {
      tradingSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleBuyUSDT = () => {
    // Navigate to purchase page with amount and currency
    window.REACT_APP_NAVIGATE('/purchase', { 
      state: { 
        amount: fromAmount, 
        currency: fromCurrency 
      } 
    });
    // Scroll to top of the page
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <section id="trading-widget" className="py-20 bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <Card className="p-8">
            {/* Title */}
            <h2 className="text-2xl font-bold text-white mb-8 text-center">{t('trading.title')}</h2>

            {/* Trading Form */}
            <div className="space-y-6">
              {/* Currency Exchange Section */}
              <div className="flex flex-col md:flex-row items-center gap-3">
                {/* From Currency */}
                <div className="w-full md:flex-1">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t('trading.youSpend')}
                  </label>
                  <div className="flex items-center bg-gray-700 rounded-lg p-4">
                    <input
                      type="number"
                      value={fromAmount}
                      onChange={(e) => handleAmountChange(e.target.value)}
                      className="flex-1 bg-transparent text-white text-xl font-semibold outline-none w-20"
                      placeholder="0"
                    />
                    <div className="relative">
                      <button
                        onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
                        className="flex items-center space-x-2 ml-3 hover:bg-gray-600 px-3 py-2 rounded-lg transition-colors cursor-pointer"
                      >
                        <img 
                          src={getCurrentCurrencyIcon()} 
                          alt={fromCurrency} 
                          className="w-6 h-6"
                        />
                        <span className="text-white font-medium">{fromCurrency}</span>
                        <i className="ri-arrow-down-s-line text-gray-400"></i>
                      </button>
                      
                      {showCurrencyDropdown && (
                        <div className="absolute top-full right-0 mt-2 w-48 bg-gray-800 rounded-lg shadow-lg z-10">
                          <div className="py-2">
                            {currencies.map((currency) => (
                              <button
                                key={currency.symbol}
                                onClick={() => handleCurrencySelect(currency.symbol)}
                                className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-700 cursor-pointer"
                              >
                                <img src={currency.icon} alt={currency.symbol} className="w-5 h-5" />
                                <span>{currency.symbol}</span>
                                <span className="text-gray-500 text-xs">{currency.name}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Swap Icon */}
                <div className="flex justify-center items-center px-2 py-2 md:py-0">
                  <div className="bg-gray-700 p-3 rounded-full">
                    <i className="ri-arrow-down-line text-white text-xl md:hidden"></i>
                    <i className="ri-arrow-right-line text-white text-xl hidden md:block"></i>
                  </div>
                </div>

                {/* To Currency - USDT */}
                <div className="w-full md:flex-1">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t('trading.youReceive')}
                  </label>
                  <div className="flex items-center bg-gray-700 rounded-lg p-4">
                    <input
                      type="number"
                      value={toAmount}
                      readOnly
                      className="flex-1 bg-transparent text-white text-xl font-semibold outline-none w-20"
                      placeholder="0"
                    />
                    <div className="flex items-center space-x-2 ml-3">
                      <img 
                        src="https://static.readdy.ai/image/75eaeb23002452c01ddd137892cf07ae/149ac95f3d8090d1ea253f9967993066.png" 
                        alt="USDT" 
                        className="w-6 h-6"
                      />
                      <span className="text-white font-medium">USDT</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Fee Information */}
              <div className="text-center text-sm text-gray-400">
                <span>{t('trading.rateUpdate')}</span>
              </div>

              {/* Action Button */}
              <Button size="lg" fullWidth className="text-lg whitespace-nowrap" onClick={handleBuyUSDT}>
                <i className="ri-shopping-cart-line mr-2"></i>
                {t('trading.button')}
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
}